import React from 'react';
import Link from 'next/link';
import { IResortPageSnowInfo } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Text } from '@/components/ui/text';
import { MiniSectionHeading } from './atoms';

export const ResortOverview = ({
  fullResort, overviewPlain, overviewHref, projectedOpening, projectedClosing,
}: {
  fullResort: IResortPageSnowInfo;
  overviewPlain: string;
  overviewHref: string;
  projectedOpening: string | null;
  projectedClosing: string | null;
}) => {
  const { t } = useOTSTranslation('common');
  if (!overviewPlain) return null;

  return (
    <section>
      <MiniSectionHeading
        title={t('resort.skiReport.resortOverview')}
        href={overviewHref}
        ctaLabel={t('resort.overview.overviewTab')}
      />
      {fullResort.image && (
        <div className="overflow-hidden rounded-md bg-muted">
          <Link href={overviewHref}>
            {/* Fixed 300px to keep the image from dominating the scroll on
                tall containers. Real <img> + descriptive alt so Google
                Images can index the resort hero (background-image is
                invisible to image search). */}
            <img
              src={fullResort.image}
              alt={fullResort.title}
              loading="lazy"
              decoding="async"
              className="h-[300px] w-full object-cover"
            />
          </Link>
        </div>
      )}
      {(projectedOpening || projectedClosing) && (
        <div className="grid grid-cols-2 divide-x divide-border border-b border-border pb-5 pt-5 text-center">
          <div>
            <div className="font-heading text-xl font-bold text-primary tabular-nums">
              {projectedOpening ?? t('common.na')}
            </div>
            <Text variant="subtle" className="text-xs text-muted-foreground">
              {t('resort.skiReport.projectedOpening')}
            </Text>
          </div>
          <div>
            <div className="font-heading text-xl font-bold text-primary tabular-nums">
              {projectedClosing ?? t('common.na')}
            </div>
            <Text variant="subtle" className="text-xs text-muted-foreground">
              {t('resort.skiReport.projectedClosing')}
            </Text>
          </div>
        </div>
      )}
      <Text variant="default" className="mt-5 line-clamp-4 leading-relaxed text-foreground/85">
        {overviewPlain}
      </Text>
      <Link
        href={overviewHref}
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        {`${t('resort.overview.readMore')} »`}
      </Link>
    </section>
  );
};
